/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { AxiosResponse } from "axios";
import { SocketIOWebSocketService } from "./websocketService";
import { attachRateLimitRetry } from "./withRateLimitRetry";

// Types and Interfaces
export interface CreateTemplateDto {
  name: string;
  subject: string;
  body: string;
  type:
    | "SYSTEM"
    | "COURSE"
    | "ADMIN"
    | "WELCOME"
    | "REMINDER"
    | "ALERT"
    | "CUSTOM";
  isActive?: boolean;
}

export interface UpdateTemplateDto {
  name?: string;
  subject?: string;
  body?: string;
  type?:
    | "SYSTEM"
    | "COURSE"
    | "ADMIN"
    | "WELCOME"
    | "REMINDER"
    | "ALERT"
    | "CUSTOM";
  isActive?: boolean;
}

export interface NotificationTemplate {
  id: string;
  name: string;
  subject: string;
  bodyTemplate: string;
  type: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TemplateSearchFilters {
  search?: string;
  type?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: "name" | "subject" | "type" | "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
}

export interface CreateNotificationDto {
  subject: string;
  body: string;
  type?: NotificationType;
  templateId?: string;
  scheduledFor?: Date;
  priority?: NotificationPriority;
  metadata?: Record<string, any>;
}

export interface SendToGroupDto extends CreateNotificationDto {
  groupId?: string;
  filters?: UserFilters;
}

export interface UserFilters {
  roles?: string[];
  ageRange?: { min?: number; max?: number };
  gender?: string[];
  ranks?: string[];
  shipNames?: string[];
  lastLoginDays?: number;
  customQuery?: string;
}

export interface CreateGroupDto {
  name: string;
  description?: string;
  filters: UserFilters;
  isActive?: boolean;
}

export interface UpdateGroupDto {
  name?: string;
  description?: string;
  filters?: UserFilters;
  isActive?: boolean;
}

export interface NotificationGroup {
  id: string;
  name: string;
  description?: string;
  filters: UserFilters;
  userCount: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface GroupSearchFilters {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: "name" | "userCount" | "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
  refreshCounts?: boolean;
}

export interface PreviewUser {
  id: string;
  name?: string;
  email: string;
  role?: string;
  // Additional fields that might be present
  firstName?: string;
  lastName?: string;
  indianCdc?: string;
  currentShipImo?: string;
  rankDesignation?: string;
  age?: number;
  gender?: string;
  mobileNumber?: string;
  currentShipName?: string;
  lastLoginAt?: string;
}

export interface PreviewUsersResponse {
  count: number;
  usersPreview: PreviewUser[];
}

export interface ScheduledNotificationsResponse {
  notifications: ScheduledNotificationData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export enum NotificationType {
  SYSTEM = "SYSTEM",
  COURSE = "COURSE",
  ADMIN = "ADMIN",
  WELCOME = "WELCOME",
  REMINDER = "REMINDER",
  ALERT = "ALERT",
}

export enum NotificationPriority {
  LOW = "LOW",
  NORMAL = "NORMAL",
  HIGH = "HIGH",
  URGENT = "URGENT",
}

export interface SendToUsersDto {
  userIds: string[];
  subject: string;
  body: string;
  type?: NotificationType;
  templateId?: string;
  scheduledFor?: Date;
  priority?: NotificationPriority;
  metadata?: Record<string, any>;
}

export interface ScheduledNotificationData {
  subject: string;
  body: string;
  type: NotificationType;
  priority?: string;
  metadata?: Record<string, any>;
  userIds: string[];
  templateId?: string;
  scheduledFor: Date;
  scheduledNotificationId: string;
  source?: {
    type: "manual" | "group" | "template";
    groupId?: string;
    adminId?: string;
  };
}

export interface NotificationResult {
  success: boolean;
  message: string;
  data?: {
    totalUsers?: number;
    deliveredCount?: number;
    failedCount?: number;
    onlineUsers?: string[];
    offlineUsers?: string[];
    scheduledNotificationId?: string;
    jobId?: string;
    scheduledFor?: Date;
  };
  error?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  timestamp: string;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// WebSocket Event Types
export interface WebSocketMessage {
  type: "NOTIFICATION" | "STATUS_UPDATE" | "CONNECTION" | "ERROR";
  data: any;
  timestamp?: Date;
  messageId?: string;
}

export interface NotificationWebSocketData {
  id?: string;
  subject: string;
  body: string;
  type?: NotificationType;
  priority?: NotificationPriority;
  timestamp?: Date;
  metadata?: any;
}

export interface UserNotification {
  id: string;
  subject: string;
  body: string;
  type: string;
  status: string;
  createdAt: string;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
}

export interface UserNotificationsResponse {
  notifications: UserNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// API Configuration
const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(apiClient);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// WebSocket Service Class using Socket.IO

// Create singleton WebSocket service instance
export const webSocketService = new SocketIOWebSocketService();

// Notification API Service
export class NotificationApiService {
  // ============================================================================
  // NOTIFICATION SENDING
  // ============================================================================

  static async sendToUsers(
    dto: SendToUsersDto
  ): Promise<ApiResponse<NotificationResult["data"]>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.post(
      "/api/notifications/users",
      dto
    );
    return response.data;
  }

  static async sendToGroup(
    dto: SendToGroupDto
  ): Promise<ApiResponse<NotificationResult["data"]>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.post(
      "/api/notifications/group",
      dto
    );
    return response.data;
  }

  // ============================================================================
  // SCHEDULED NOTIFICATIONS
  // ============================================================================

  static async listScheduledNotifications(
    status?: string,
    page: number = 1,
    limit: number = 20
  ): Promise<ApiResponse<ScheduledNotificationsResponse>> {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response: AxiosResponse<ApiResponse<ScheduledNotificationsResponse>> = await apiClient.get(
      `/api/notifications/scheduled?${params.toString()}`
    );
    return response.data;
  }

  static async getScheduledNotificationStatus(
    scheduledNotificationId: string
  ): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/scheduled/${scheduledNotificationId}/status`
    );
    return response.data;
  }

  static async cancelScheduledNotification(
    scheduledNotificationId: string
  ): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await apiClient.delete(
      `/api/notifications/scheduled/${scheduledNotificationId}`
    );
    return response.data;
  }

  // ============================================================================
  // TEMPLATE MANAGEMENT
  // ============================================================================

  static async createTemplate(
    dto: CreateTemplateDto
  ): Promise<ApiResponse<NotificationTemplate>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.post(
      "/api/notifications/templates",
      dto
    );
    return response.data;
  }

  static async getTemplates(
    filters?: TemplateSearchFilters
  ): Promise<ApiResponse<NotificationTemplate[]>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/templates?${params.toString()}`
    );
    return response.data;
  }

  static async getTemplate(
    templateId: string
  ): Promise<ApiResponse<NotificationTemplate>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/templates/${templateId}`
    );
    return response.data;
  }

  static async updateTemplate(
    templateId: string,
    dto: UpdateTemplateDto
  ): Promise<ApiResponse<NotificationTemplate>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.put(
      `/api/notifications/templates/${templateId}`,
      dto
    );
    return response.data;
  }

  static async deleteTemplate(templateId: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await apiClient.delete(
      `/api/notifications/templates/${templateId}`
    );
    return response.data;
  }

  // ============================================================================
  // GROUP MANAGEMENT
  // ============================================================================

  static async createGroup(
    dto: CreateGroupDto
  ): Promise<ApiResponse<NotificationGroup>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.post(
      "/api/notifications/groups",
      dto
    );
    return response.data;
  }

  static async getGroups(
    filters?: GroupSearchFilters
  ): Promise<ApiResponse<NotificationGroup[]>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/groups?${params.toString()}`
    );
    return response.data;
  }

  static async getGroup(
    groupId: string
  ): Promise<ApiResponse<NotificationGroup>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/groups/${groupId}`
    );
    return response.data;
  }

  static async updateGroup(
    groupId: string,
    dto: UpdateGroupDto
  ): Promise<ApiResponse<NotificationGroup>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.put(
      `/api/notifications/groups/${groupId}`,
      dto
    );
    return response.data;
  }

  static async deleteGroup(groupId: string): Promise<ApiResponse> {
    const response: AxiosResponse<ApiResponse> = await apiClient.delete(
      `/api/notifications/groups/${groupId}`
    );
    return response.data;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Preview users based on filters for group creation
   */
  static async previewUsers(filters: UserFilters): Promise<ApiResponse<PreviewUsersResponse>> {
    const response: AxiosResponse<ApiResponse<PreviewUsersResponse>> = await apiClient.post(
      `/api/notifications/groups/previewUsers`,
      { filters }
    );
    return response.data;
  }

  // ============================================================================
  // USER NOTIFICATION METHODS
  // ============================================================================

  /**
   * Get notifications for the current user
   */
  static async getUserNotifications(
    page: number = 1,
    limit: number = 20,
    unreadOnly: boolean = false
  ): Promise<ApiResponse<UserNotification[]>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    params.append("unreadOnly", unreadOnly.toString());

    const response: AxiosResponse<ApiResponse<UserNotification[]>> =
      await apiClient.get(`/api/notifications?${params.toString()}`);
    return response.data;
  }

  /**
   * Get unread notification count for the current user
   */
  static async getUnreadCount(): Promise<ApiResponse<{ count: number }>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      "/api/notifications/unread-count"
    );
    return response.data;
  }

  /**
   * Mark a specific notification as read
   */
  static async markAsRead(
    notificationId: string
  ): Promise<ApiResponse<UserNotification>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.put(
      `/api/notifications/${notificationId}/read`
    );
    return response.data;
  }

  /**
   * Mark all notifications as read for the current user
   */
  static async markAllAsRead(): Promise<ApiResponse<{ updatedCount: number }>> {
    const response: AxiosResponse<ApiResponse> = await apiClient.put(
      "/api/notifications/mark-all-read"
    );
    return response.data;
  }

  /**
   * Get sent notifications (admin view)
   */
  static async getSentNotifications(
    page: number = 1,
    limit: number = 20,
    status?: string,
    type?: string,
    search?: string
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (status) params.append("status", status);
    if (type) params.append("type", type);
    if (search) params.append("search", search);

    const response: AxiosResponse<ApiResponse> = await apiClient.get(
      `/api/notifications/sent?${params.toString()}`
    );
    return response.data;
  }
}

export default NotificationApiService;
